<template>
	<app-icon
		class="ml-3 cursor-pointer hover:text-blue-400"
		:icon="['fas', 'fa-pencil']"
		@click="open = true"
	/>

	<slideover title="Edit Stock ID" :open="open" @close="close">
		<app-label>Picture</app-label>

		<div v-if="picturePreview" class="w-80 mb-4">
			<img
				:src="picturePreview"
				alt="Stock Image"
				class="w-full object-contain rounded"
			/>
		</div>

		<div
			class="p-4 border rounded border-dashed text-center mb-4"
			@paste="handlePaste"
			@dragover.prevent
			@drop.prevent="handleDrop"
		>
			<p class="text-gray-500">
				Paste or drag an image here
			</p>
		</div>

		<app-file-button name="Picture" @file-change="setPicture">
			{{ values.picture ? 'Change Picture' : 'Add Picture' }}
		</app-file-button>

		<p v-if="errors.picture" class="text-red-500 text-sm mt-2">
			{{ errors.picture }}
		</p>


		<app-input
			class="mt-5"
			label="Finish"
			:model-value="values.finish"
			:disabled="submitting"
			:error="errors['finish']"
			@update:model-value="(value) => setFieldValue('finish', value)"
		/>

		<app-input
			label="ID Description"
			type="textarea"
			:model-value="values.id_description"
			:disabled="submitting"
			:error="errors['id_description']"
			@update:model-value="(value) =>
        setFieldValue('id_description', value)"
		/>

		<template #action>
			<div class="flex justify-end w-full">
				<app-button :disabled="submitting" @click="submit">
					Save
				</app-button>
			</div>
		</template>
	</slideover>
</template>

<script lang="ts" setup>
	import { ref, computed, watch } from 'vue'
	import useToaster from '@/stores/toaster'
	import { useForm } from 'vee-validate'
	import * as Yup from 'yup'
	import AppFileButton from '@/components/base/forms/AppFileButton.vue'
	import SubgroupService from '@/services/subgroup'

	const toaster = useToaster()
	const submitting = ref(false)
	const open = ref(false)

	const props = defineProps<{
		subgroup: any | null
	}>()

	const emit = defineEmits(['completed'])

	const subgroupService = new SubgroupService()

	/*
	|--------------------------------------------------------------------------
	| Form
	|--------------------------------------------------------------------------
	*/

	const {
		handleSubmit,
		resetForm,
		values,
		errors,
		setFieldValue
	} = useForm<{
		picture: File | null
		finish: string
		id_description: string
	}>({
		initialValues: {
			picture: null,
			finish: props.subgroup?.profiles?.finish ?? '',
			id_description:
				props.subgroup?.profiles?.id_description ?? ''
		},
		validationSchema: Yup.object({
			picture: Yup.mixed<File>()
				.nullable()
				.test(
					'required-if-missing',
					'A picture is required',
					function (value) {
						if (props.subgroup?.picture) {
							return true
						}
						return !!value
					}
				)
				.test(
					'fileType',
					'Only image files are allowed',
					function (value) {
						if (!value) return true
						return value.type.startsWith('image/')
					}
				)
				.test(
					'fileSize',
					'Image must be under 5MB',
					function (value) {
						if (!value) return true
						return value.size <= 5 * 1024 * 1024
					}
				),
			finish: Yup.string().required('Finish is required'),
			id_description: Yup.string().required(
				'ID Description is required'
			)
		})
	})

	/*
	|--------------------------------------------------------------------------
	| Picture Preview (Reactive & Clean)
	|--------------------------------------------------------------------------
	*/

	const picturePreview = computed(() => {
		if (values.picture) {
			return URL.createObjectURL(values.picture)
		}
		return props.subgroup?.picture ?? null
	})

	/*
	|--------------------------------------------------------------------------
	| Keep form in sync when subgroup changes
	|--------------------------------------------------------------------------
	*/

	watch(open, (isOpen) => {
		if (!isOpen || !props.subgroup) return

		resetForm({
			values: {
				picture: null,
				finish: props.subgroup.profiles?.finish ?? '',
				id_description:
					props.subgroup.profiles?.id_description ?? ''
			}
		})
	})

	/*
	|--------------------------------------------------------------------------
	| Methods
	|--------------------------------------------------------------------------
	*/

	function close() {
		open.value = false
		resetForm()
	}

	function setPicture(value: File | File[]) {
		const file = Array.isArray(value) ? value[0] : value
		if (!file) return

		setFieldValue('picture', file, true) // ✅ mark as touched
	}

	function handlePaste(e: ClipboardEvent) {
		const items = e.clipboardData?.items
		if (!items) return

		for (const item of items) {
			if (item.type.startsWith('image/')) {
				const file = item.getAsFile()
				if (file) setPicture(file)
				break
			}
		}
	}

	function handleDrop(e: DragEvent) {
		const file = e.dataTransfer?.files?.[0]
		if (file && file.type.startsWith('image/')) {
			setPicture(file)
		}
	}

	/*
	|--------------------------------------------------------------------------
	| Submit
	|--------------------------------------------------------------------------
	*/

	const submit = handleSubmit(
		async (formValues) => {
			submitting.value = true

			try {
				const formData = new FormData()

				if (formValues.picture) {
					formData.append('picture', formValues.picture)
				}

				formData.append('finish', formValues.finish)
				formData.append(
					'id_description',
					formValues.id_description
				)

				const result = await subgroupService.updateStockId(
					props.subgroup.code,
					formData
				)

				toaster.success({ text: result.data.message })

				close()
				emit('completed', result.data)
			} catch (error) {
				console.error(error)
			} finally {
				submitting.value = false
			}
		},
		(validationErrors) => {
			console.error(validationErrors)
		}
	)
</script>